import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { NumberField } from "../ConfigFields/NumberField";
import { ArrayObjectField } from "../ConfigFields/ArrayObjectField";
import { useAuth } from "../../../../hooks/AuthProvider";
import { WOOCOMMERCE_STATUS_ENDPOINT } from "../../../../config/urls";

const SHIPPING_SOURCE_OPTIONS = [
  { value: "woocommerce", label: "WooCommerce" },
  { value: "shopify", label: "Shopify Marketing (disabled)", disabled: true },
  { value: "magento", label: "Magento (disabled)", disabled: true },
  { value: "custom", label: "Custom" },
];

const CUSTOM_FIELD_ORDER = [
  "shipping_charge_per_item",
  "bulk_shipping_charge",
  "pincode_specific_shipping_charges",
  "city_specific_shipping_charges",
  "state_specific_shipping_charges",
];

const ShippingChargeCalculationPreset = ({ schema, config, handleConfigChange }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const shippingSource = config?.shipping_source || "custom";
  const [wooCommerceStatus, setWooCommerceStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const token = await getValidToken();
        const response = await fetch(WOOCOMMERCE_STATUS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch WooCommerce status");

        const data = await response.json();
        if (!cancelled) setWooCommerceStatus(data);
      } catch (err) {
        console.error("Failed to fetch WooCommerce integration status:", err);
        if (!cancelled) setWooCommerceStatus({ connected: false });
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  const properties = schema?.properties || {};

  return (
    <Stack spacing={3}>
      <FormControl fullWidth size="small">
        <InputLabel id="shipping-source-label">Shipping Source</InputLabel>
        <Select
          labelId="shipping-source-label"
          label="Shipping Source"
          value={shippingSource}
          onChange={(e) => handleConfigChange("shipping_source", e.target.value)}
        >
          {SHIPPING_SOURCE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {shippingSource === "woocommerce" && (
        <Box>
          {statusLoading ? (
            <CircularProgress size={20} />
          ) : wooCommerceStatus?.connected ? (
            <Alert severity="success">
              WooCommerce is integrated{wooCommerceStatus.site_url ? ` (${wooCommerceStatus.site_url})` : ""}.
              Shipping charges for this order will be calculated using the rates configured on your
              WooCommerce store.
            </Alert>
          ) : (
            <Alert severity="warning">
              WooCommerce is not integrated yet. Please integrate WooCommerce from the Integrations
              page to use it for shipping charge calculation.
            </Alert>
          )}
        </Box>
      )}

      {shippingSource === "custom" && (
        <Stack spacing={3}>
          <Typography sx={{ fontWeight: 600, mb: -1 }}>Custom Shipping Rules</Typography>

          {CUSTOM_FIELD_ORDER.filter((key) => properties[key]).map((key) => {
            const fieldSchema = properties[key];

            if (fieldSchema.type === "number") {
              return (
                <NumberField
                  key={key}
                  fieldKey={key}
                  schema={fieldSchema}
                  value={config?.[key]}
                  required={key === "shipping_charge_per_item"}
                  onChange={(val) => handleConfigChange(key, val)}
                />
              );
            }

            if (fieldSchema.type === "array") {
              return (
                <ArrayObjectField
                  key={key}
                  label={fieldSchema.description}
                  schema={fieldSchema}
                  value={config?.[key] || []}
                  onChange={(val) => handleConfigChange(key, val)}
                />
              );
            }

            return null;
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default ShippingChargeCalculationPreset;
